import { useState } from 'react';
import { ConnectionState } from '@/state/types';
import { useStationStore } from '@/state/useStationStore';
import { toast } from '@/state/useToastStore';
import { AuthBanner } from '@/ui/components/AuthBanner';
import { EVSECard } from '@/ui/components/EVSECard';
import { PowerSection } from '@/ui/components/PowerSection';
import { ActiveTransactionsList } from '@/ui/components/ActiveTransactionsList';
import { TransactionPropertiesPanel } from '@/ui/components/TransactionPropertiesPanel';
import { TransactionChart } from '@/ui/components/TransactionChart';
import { PlugAndChargePanel } from '@/ui/components/PlugAndChargePanel';
import { isV2x } from '@/lib/ocppProfile';

export function ChargePointTab(): JSX.Element {
  const evses = useStationStore((s) => s.evses);
  const connectors = useStationStore((s) => s.connectors);
  const connectionState = useStationStore((s) => s.connectionState);
  const ocppVersion = useStationStore((s) => s.ocppVersion);
  const heartbeat = useStationStore((s) => s.heartbeat);
  const [heartbeating, setHeartbeating] = useState(false);
  const isConnected = connectionState === ConnectionState.Connected;
  const activeConnectorIds = [...connectors.values()]
    .filter((c) => c.activeTransactionId !== null)
    .map((c) => c.id);

  const handleHeartbeat = (): void => {
    setHeartbeating(true);
    void heartbeat()
      .then(() => toast('Heartbeat sent'))
      .finally(() => setHeartbeating(false));
  };

  return (
    <div
      role="tabpanel"
      id="tab-panel-chargepoint"
      aria-labelledby="tab-chargepoint"
      className="space-y-4 p-4"
    >
      <div className="flex justify-end">
        {/* NIT-008 — manual Heartbeat trigger. Disabled when disconnected. */}
        <button
          type="button"
          onClick={handleHeartbeat}
          disabled={!isConnected || heartbeating}
          title={isConnected ? 'Send a Heartbeat now' : 'Connect first'}
          className="inline-flex items-center gap-1.5 rounded bg-slate-700 px-3 py-1 text-xs text-slate-100 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-300 disabled:cursor-not-allowed disabled:opacity-40"
          data-testid="manual-heartbeat"
        >
          {/* VC-G — vector heart instead of an emoji (CLAUDE.md §6: no emoji in UI). */}
          <svg viewBox="0 0 16 16" aria-hidden="true" className="h-3 w-3 fill-current">
            <path d="M8 14.25l-.97-.86C3.6 10.3 1.5 8.42 1.5 6.12 1.5 4.3 2.92 2.9 4.75 2.9c1.03 0 2.02.48 2.66 1.24h1.18A3.53 3.53 0 0 1 11.25 2.9c1.83 0 3.25 1.4 3.25 3.22 0 2.3-2.1 4.18-5.53 7.27l-.97.86z" />
          </svg>
          {heartbeating ? 'Heartbeat…' : 'Heartbeat'}
        </button>
      </div>
      <AuthBanner />
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-4">
          {evses.map((evse) => (
            <EVSECard key={evse.id} evse={evse} />
          ))}
        </div>
        <div className="space-y-4">
          <PowerSection />
          <ActiveTransactionsList />
          {activeConnectorIds.map((cid) => (
            <div key={cid} className="space-y-4">
              <TransactionPropertiesPanel connectorId={cid} />
              <TransactionChart connectorId={cid} />
            </div>
          ))}
          {isV2x(ocppVersion) ? <PlugAndChargePanel /> : null}
        </div>
      </div>
    </div>
  );
}
